import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useQuery } from '@tanstack/react-query'
import dagre from 'dagre'
import React, {
  useEffect,
  useMemo,
  useRef,
  type ComponentType,
  type ReactNode,
} from 'react'
import {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
  type ReactFlowInstance,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  IconAlertCircle,
  IconChartBar,
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconCode,
  IconColumns,
  IconDatabase,
  IconGitBranch,
  IconMathFunction,
  IconPlug,
  IconSearch,
  IconTable,
  IconTarget,
} from '@tabler/icons-react'
import { useSearchParams } from 'react-router-dom'
import {
  listAllTables,
  type UnityCatalogTable,
} from '../../api/databricks'
import { type LineageEdge, type LineageNode } from '../../api/intelligence'
import {
  dbtCloudApi,
  type ColumnLineageEntry,
  type ColumnLineageMap,
  type UpdateFrequencyMap,
} from '../../api/dbtCloud'
import { SqlEditor } from '../query/SqlEditor'
import { extractColumnSnippet } from './lineageUtils'
import { Empty, IntelligencePageHeader, QueryState } from './shared'

class RenderErrorBoundary extends React.Component<
  { children: ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[Lineage render error]', error, info)
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert color="red" title="Lineage render error">
          {this.state.error?.message ??
            'The lineage graph could not be rendered.'}
        </Alert>
      )
    }
    return this.props.children
  }
}

const DBT_LAYERS = [
  { label: 'Sources', types: ['source', 'seed'] },
  { label: 'Bronze', types: ['bronze'] },
  { label: 'Silver', types: ['silver'] },
  { label: 'Gold', types: ['gold'] },
  { label: 'Semantic', types: ['semantic'] },
  { label: 'Metrics', types: ['metric'] },
  { label: 'Utilities', types: ['utilities'] },
]

interface NodeColor {
  bg: string
  c: string
}

interface NodeIconProps {
  size?: number | string
  color?: string
}

interface LineageConfig {
  layers: { label: string; types: string[] }[]
  nodeColors: Record<string, NodeColor>
  nodeIcons: Record<string, ComponentType<NodeIconProps>>
  edgeColors: Record<string, string>
}

const dbtConfig: LineageConfig = {
  layers: DBT_LAYERS,
  nodeColors: {
    source: { bg: 'blue.0', c: 'blue.7' },
    seed: { bg: 'grape.0', c: 'grape.7' },
    bronze: { bg: 'orange.0', c: 'orange.7' },
    silver: { bg: 'gray.0', c: 'gray.7' },
    gold: { bg: 'yellow.0', c: 'yellow.7' },
    semantic: { bg: 'teal.0', c: 'teal.7' },
    metric: { bg: 'indigo.0', c: 'indigo.7' },
    utilities: { bg: 'pink.0', c: 'pink.7' },
  },
  nodeIcons: {
    source: IconDatabase,
    seed: IconTable,
    bronze: IconTable,
    silver: IconTable,
    gold: IconChartBar,
    semantic: IconMathFunction,
    metric: IconTarget,
    utilities: IconClock,
  },
  edgeColors: {
    source: 'blue',
    ref: 'prism',
  },
}

function normalizeType(value?: string) {
  return value ?? 'unknown'
}

// Known naming-convention prefixes used for dbt schemas (e.g. "bronze_",
// "silver_", "gold_"). The "schema domain" for a node is derived from its
// schema (LineageNode.system) rather than its table name, following the
// part of the schema that comes after the prefix and underscore, e.g.
// "gold_marketing" -> "marketing". Schemas that don't match a known prefix
// (e.g. "marketing", "enterprise") are used as-is.
const KNOWN_SCHEMA_PREFIXES = [
  'dim',
  'fact',
  'fct',
  'report',
  'stg',
  'brz',
  'bronze',
  'slv',
  'silver',
  'gld',
  'gold',
]

function schemaDomain(schema?: string): string | undefined {
  if (!schema) return undefined
  const lower = schema.toLowerCase()
  for (const prefix of KNOWN_SCHEMA_PREFIXES) {
    if (lower.startsWith(`${prefix}_`)) {
      return lower.slice(prefix.length + 1)
    }
  }
  return lower
}

// Source (and seed) nodes represent raw external systems rather than a dbt
// schema, so they never define -- or are matched against -- a schema
// domain. They're still shown in a filtered graph, though, as long as
// they're directly connected to a node that belongs to the selected domain.
function isSourceNode(node: LineageNode): boolean {
  return node.type === 'source' || node.type === 'seed'
}

// Nodes that don't belong to a single schema domain: raw sources/seeds,
// semantic-layer models, and metrics (which are not materialized into a
// schema). Like sources, they ride along in a domain-filtered graph when
// directly connected to a node in the selected domain -- otherwise selecting
// a domain (e.g. via picking an asset) would drop the semantic/metric node
// attached to that domain's models.
function isDomainlessNode(node: LineageNode): boolean {
  return isSourceNode(node) || node.type === 'semantic' || node.type === 'metric'
}

/**
 * Filter lineage to only include nodes connected to source nodes whose
 * system name matches the selected source. Walks downstream from the
 * matching source nodes to include all reachable nodes.
 */
function filterBySource(
  nodes: LineageNode[],
  edges: LineageEdge[],
  source: string | null
): { nodes: LineageNode[]; edges: LineageEdge[] } {
  if (!source) return { nodes, edges }

  // Find source nodes matching the selected system name
  const matchingSources = nodes.filter(
    (n) => isSourceNode(n) && n.system === source
  )
  if (matchingSources.length === 0) return { nodes: [], edges: [] }

  // BFS downstream from matching source nodes to find all reachable nodes
  const childrenMap = new Map<string, string[]>()
  for (const edge of edges) {
    if (!childrenMap.has(edge.source)) childrenMap.set(edge.source, [])
    childrenMap.get(edge.source)!.push(edge.target)
  }

  const reachable = new Set(matchingSources.map((n) => n.id))
  const queue = [...reachable]
  while (queue.length > 0) {
    const current = queue.shift()!
    for (const child of childrenMap.get(current) ?? []) {
      if (!reachable.has(child)) {
        reachable.add(child)
        queue.push(child)
      }
    }
  }

  const filteredNodes = nodes.filter((n) => reachable.has(n.id))
  const filteredEdges = edges.filter(
    (e) => reachable.has(e.source) && reachable.has(e.target)
  )
  return { nodes: filteredNodes, edges: filteredEdges }
}

function filterByDomain(
  nodes: LineageNode[],
  edges: LineageEdge[],
  domain: string | null
): { nodes: LineageNode[]; edges: LineageEdge[] } {
  if (!domain) return { nodes, edges }
  const domainNodes = nodes.filter(
    (node) => !isDomainlessNode(node) && schemaDomain(node.system) === domain
  )
  const domainIds = new Set(domainNodes.map((node) => node.id))
  const connectedDomainlessNodes = nodes.filter(
    (node) =>
      isDomainlessNode(node) &&
      edges.some(
        (edge) =>
          (edge.source === node.id && domainIds.has(edge.target)) ||
          (edge.target === node.id && domainIds.has(edge.source))
      )
  )
  const filteredNodes = [...domainNodes, ...connectedDomainlessNodes]
  const nodeIds = new Set(filteredNodes.map((node) => node.id))
  const filteredEdges = edges.filter(
    (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target)
  )
  return { nodes: filteredNodes, edges: filteredEdges }
}

function nodeStyle(config: LineageConfig, node: LineageNode) {
  const type = normalizeType(node.type)
  return {
    style: config.nodeColors[type] ?? { bg: 'gray.0', c: 'gray.7' },
    Icon: config.nodeIcons[type] ?? IconDatabase,
  }
}

const NODE_WIDTH = 160
const NODE_PADDING_Y = 8
const COLUMN_ROW_HEIGHT = 13
const MAX_INLINE_COLUMNS = 5

function DagreNode({ data }: NodeProps) {
  const {
    node: lineageNode,
    table,
    height,
    selected,
    onNodeClick,
    lastRunTs,
    frequency,
  } = data as {
    node: LineageNode
    table?: UnityCatalogTable
    height: number
    selected?: boolean
    onNodeClick?: (id: string) => void
    lastRunTs?: string
    frequency?: string
  }
  const { style, Icon } = nodeStyle(dbtConfig, lineageNode)
  const colorName = style.c.split('.')[0]
  const highlightColor = selected ? 'prism' : colorName
  const schemaName = table?.schema_name ?? lineageNode.system
  const columns = (table?.columns ?? []).slice(0, MAX_INLINE_COLUMNS)
  const hasMoreColumns =
    table?.columns && table.columns.length > MAX_INLINE_COLUMNS

  return (
    <Box
      bg={style.bg}
      pt={4}
      px={4}
      pb={1}
      style={{
        width: NODE_WIDTH,
        height,
        borderRadius: '6px',
        border: selected
          ? `2px solid var(--mantine-color-${highlightColor}-7)`
          : '1px solid var(--mantine-color-gray-3)',
        borderLeft: `4px solid var(--mantine-color-${highlightColor}-7)`,
        cursor: onNodeClick ? 'pointer' : 'default',
      }}
      onClick={() => onNodeClick?.(lineageNode.id)}
    >
      <Handle id="target-left" type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle id="target-right" type="target" position={Position.Right} style={{ opacity: 0 }} />
      <Group gap={4} wrap="nowrap">
        <ThemeIcon
          variant="transparent"
          color={highlightColor as 'blue'}
          size="sm"
        >
          <Icon size={14} color={`var(--mantine-color-${highlightColor}-7)`} />
        </ThemeIcon>
        <Tooltip
          label={`${lineageNode.name ?? lineageNode.id}${lineageNode.system ? ` (${lineageNode.system})` : ''}`}
          withArrow
        >
          <Text
            size="12px"
            fw={700}
            c={style.c}
            lineClamp={1}
            lh="12px"
            style={{ flex: 1 }}
          >
            {lineageNode.name ?? lineageNode.id}
          </Text>
        </Tooltip>
      </Group>
      {schemaName && (
        <Group gap={4} wrap="nowrap" mt={2}>
          <Text size="8px" fw={600} c="dimmed" lineClamp={1}>
            Schema:
          </Text>
          <Text size="10px" fw={600} c={style.c} lineClamp={1} lh="10px">
            {schemaName}
          </Text>
        </Group>
      )}
      {lineageNode.type && (
        <Group gap={4} wrap="nowrap" mt={1}>
          <Text size="8px" fw={600} c="dimmed" lineClamp={1} lh="9px">
            Type:
          </Text>
          <Badge
            size="xs"
            variant="light"
            color={colorName}
            styles={{
              root: {
                height: '14px',
                fontSize: '8px',
                lineHeight: '10px',
                padding: '0 4px',
              },
            }}
          >
            {lineageNode.type}
          </Badge>
        </Group>
      )}
      {(frequency || lastRunTs) && (
        <Group gap={4} wrap="nowrap" mt={1}>
          <IconClock size={8} color="var(--mantine-color-dimmed)" />
          <Text size="8px" fw={600} c="dimmed" lineClamp={1} lh="9px">
            {frequency ?? (lastRunTs ? new Date(lastRunTs).toLocaleDateString() : '')}
          </Text>
        </Group>
      )}
      {columns.length > 0 && (
        <Stack gap={0} mt={1}>
          {columns.map((column) => (
            <Group key={column.name} gap={2} wrap="nowrap">
              <Text
                size="7px"
                fw={600}
                c={style.c}
                lineClamp={1}
                lh="9px"
                style={{ flex: 1 }}
              >
                {column.name}
              </Text>
              <Badge
                variant="light"
                color="gray"
                size="xs"
                styles={{
                  root: {
                    padding: '0 2px',
                    height: '10px',
                    fontSize: '7px',
                    lineHeight: '9px',
                  },
                }}
              >
                {column.type_text ?? column.type_name ?? '?'}
              </Badge>
            </Group>
          ))}
          {hasMoreColumns && (
            <Text size="7px" c={style.c} opacity={0.7} lh="9px">
              +{table!.columns!.length - MAX_INLINE_COLUMNS} more
            </Text>
          )}
        </Stack>
      )}
      <Handle id="source-right" type="source" position={Position.Right} style={{ opacity: 0 }} />
      <Handle id="source-left" type="source" position={Position.Left} style={{ opacity: 0 }} />
    </Box>
  )
}

function SwimLaneLabel({ data }: NodeProps) {
  const { label } = data as { label: string }
  return (
    <Box
      px={8}
      py={4}
      style={{
        width: NODE_WIDTH,
        textAlign: 'center',
        borderRadius: '4px',
        background: 'var(--mantine-color-gray-1)',
        border: '1px dashed var(--mantine-color-gray-4)',
        pointerEvents: 'none',
      }}
    >
      <Text size="11px" fw={700} c="dimmed" tt="uppercase" lh="16px">
        {label}
      </Text>
    </Box>
  )
}

function SwimLaneDivider({ data }: NodeProps) {
  const { height } = data as { height: number }
  return (
    <Box
      style={{
        width: 1,
        height,
        borderLeft: '1px dashed var(--mantine-color-gray-4)',
        pointerEvents: 'none',
      }}
    />
  )
}

const nodeTypes = {
  dbtNode: DagreNode,
  swimLaneLabel: SwimLaneLabel,
  swimLaneDivider: SwimLaneDivider,
}

function layerRank(type?: string): number {
  const t = normalizeType(type)
  for (let i = 0; i < DBT_LAYERS.length; i++) {
    if (DBT_LAYERS[i].types.includes(t)) return i
  }
  return DBT_LAYERS.length - 1
}

function nodeHeight(node: LineageNode, table?: UnityCatalogTable, hasFrequency?: boolean) {
  const schemaName = table?.schema_name ?? node.system
  const columns = (table?.columns ?? []).slice(0, MAX_INLINE_COLUMNS)
  const hasMoreColumns = (table?.columns?.length ?? 0) > MAX_INLINE_COLUMNS
  let height = NODE_PADDING_Y + 14
  if (schemaName) height += 14
  if (node.type) height += 16
  if (hasFrequency) height += 14
  if (columns.length > 0) {
    height += 2
    height += columns.length * COLUMN_ROW_HEIGHT
  }
  if (hasMoreColumns) height += 10
  return height
}

function findUnityCatalogTable(
  tables: UnityCatalogTable[] | undefined,
  node: LineageNode
): UnityCatalogTable | undefined {
  if (!node.name || !tables) return undefined
  if (node.system) {
    const bySchema = tables.find(
      (table) =>
        table.name === node.name &&
        table.schema_name.toLowerCase() === node.system!.toLowerCase()
    )
    if (bySchema) return bySchema
  }
  return tables.find((table) => table.name === node.name)
}

function buildReactFlowElements(
  nodes: LineageNode[],
  edges: LineageEdge[],
  onNodeClick?: (id: string) => void,
  tables?: UnityCatalogTable[],
  selectedNodeId?: string,
  updateFrequency?: UpdateFrequencyMap
): { nodes: Node[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph()
  g.setGraph({ rankdir: 'LR', ranksep: 80, nodesep: 20 })
  g.setDefaultEdgeLabel(() => ({}))

  const nodeTable = (node: LineageNode) => findUnityCatalogTable(tables, node)

  for (const node of nodes) {
    const table = nodeTable(node)
    const hasFreq = Boolean(
      updateFrequency?.lastRun.get(node.id) ||
      updateFrequency?.frequencies.get(node.id)
    )
    g.setNode(node.id, {
      width: NODE_WIDTH,
      height: nodeHeight(node, table, hasFreq),
      rank: layerRank(node.type),
    })
  }

  for (const edge of edges) {
    g.setEdge(edge.source, edge.target)
  }

  dagre.layout(g)

  // ---- Group nodes by lane and compute each lane's x-extent ----
  const LANE_GAP = 80
  const laneBuckets = new Map<number, LineageNode[]>()
  for (const node of nodes) {
    const rank = layerRank(node.type)
    if (!laneBuckets.has(rank)) laneBuckets.set(rank, [])
    laneBuckets.get(rank)!.push(node)
  }
  const sortedRanks = Array.from(laneBuckets.keys()).sort((a, b) => a - b)

  // For each lane, find the min/max x that dagre assigned (center-based).
  // The lane width is the span from the leftmost node edge to the rightmost
  // node edge, allowing the lane to expand when dagre spreads nodes out.
  const laneMinX = new Map<number, number>()
  const laneMaxX = new Map<number, number>()
  for (const rank of sortedRanks) {
    let minX = Infinity
    let maxX = -Infinity
    for (const node of laneBuckets.get(rank)!) {
      const cx = g.node(node.id).x
      minX = Math.min(minX, cx - NODE_WIDTH / 2)
      maxX = Math.max(maxX, cx + NODE_WIDTH / 2)
    }
    laneMinX.set(rank, minX)
    laneMaxX.set(rank, maxX)
  }

  // Repack lanes left-to-right so they sit next to each other with a gap,
  // preserving dagre's relative positions within each lane.
  const laneShift = new Map<number, number>()
  let cursor = 0
  for (const rank of sortedRanks) {
    const shift = cursor - laneMinX.get(rank)!
    laneShift.set(rank, shift)
    const laneWidth = laneMaxX.get(rank)! - laneMinX.get(rank)!
    cursor += laneWidth + LANE_GAP
  }

  // ---- Compute final positions ----
  const positionMap = new Map<string, { x: number; y: number }>()
  for (const node of nodes) {
    const rank = layerRank(node.type)
    const positioned = g.node(node.id)
    const table = nodeTable(node)
    const hasFreq = Boolean(
      updateFrequency?.lastRun.get(node.id) ||
      updateFrequency?.frequencies.get(node.id)
    )
    const height = nodeHeight(node, table, hasFreq)
    positionMap.set(node.id, {
      x: positioned.x - NODE_WIDTH / 2 + laneShift.get(rank)!,
      y: positioned.y - height / 2,
    })
  }

  // ---- Compute vertical extent of the graph ----
  const LANE_LABEL_HEIGHT = 28
  const LANE_LABEL_PAD = 12
  let globalMinY = Infinity
  let globalMaxY = -Infinity
  for (const node of nodes) {
    const pos = positionMap.get(node.id)!
    const table = nodeTable(node)
    const hasFreq = Boolean(
      updateFrequency?.lastRun.get(node.id) ||
      updateFrequency?.frequencies.get(node.id)
    )
    const height = nodeHeight(node, table, hasFreq)
    if (pos.y < globalMinY) globalMinY = pos.y
    if (pos.y + height > globalMaxY) globalMaxY = pos.y + height
  }
  const graphHeight = globalMaxY - globalMinY

  // ---- Build swim-lane label nodes ----
  const laneLabels: Node[] = sortedRanks.map((rank) => {
    const layer = DBT_LAYERS[rank]
    const laneCenterX =
      (laneMinX.get(rank)! + laneMaxX.get(rank)!) / 2 +
      laneShift.get(rank)!
    return {
      id: `__lane_label_${rank}`,
      type: 'swimLaneLabel',
      position: {
        x: laneCenterX - NODE_WIDTH / 2,
        y: globalMinY - LANE_LABEL_HEIGHT - LANE_LABEL_PAD,
      },
      data: { label: layer?.label ?? `Layer ${rank}` },
      draggable: false,
      selectable: false,
      connectable: false,
    }
  })

  // ---- Build swim-lane divider lines between lanes ----
  const dividerPad = 40
  const dividerHeight = LANE_LABEL_HEIGHT + LANE_LABEL_PAD + graphHeight + dividerPad
  const laneDividers: Node[] = []
  for (let i = 0; i < sortedRanks.length - 1; i++) {
    const rank = sortedRanks[i]
    const nextRank = sortedRanks[i + 1]
    // Place divider halfway between the right edge of this lane and
    // the left edge of the next lane (after shifts).
    const rightEdge = laneMaxX.get(rank)! + laneShift.get(rank)!
    const leftEdge = laneMinX.get(nextRank)! + laneShift.get(nextRank)!
    const dividerX = (rightEdge + leftEdge) / 2
    laneDividers.push({
      id: `__lane_divider_${rank}`,
      type: 'swimLaneDivider',
      position: {
        x: dividerX,
        y: globalMinY - LANE_LABEL_HEIGHT - LANE_LABEL_PAD,
      },
      data: { height: dividerHeight },
      draggable: false,
      selectable: false,
      connectable: false,
    })
  }

  // ---- Build ReactFlow nodes & edges ----
  const flowNodes: Node[] = nodes.map((node) => {
    const table = nodeTable(node)
    const lastRunTs = updateFrequency?.lastRun.get(node.id)
    const frequency = updateFrequency?.frequencies.get(node.id)
    const hasFrequency = Boolean(lastRunTs || frequency)
    const height = nodeHeight(node, table, hasFrequency)
    const pos = positionMap.get(node.id)!
    return {
      id: node.id,
      type: 'dbtNode',
      position: { x: pos.x, y: pos.y },
      data: {
        node,
        table,
        height,
        selected: node.id === selectedNodeId,
        onNodeClick,
        lastRunTs,
        frequency,
      },
      draggable: true,
    }
  })

  const flowEdges: Edge[] = edges.map((edge, idx) => {
    const type = edge.transformation_type ?? edge.type ?? edge.kind ?? 'ref'
    return {
      id: `${edge.source}->${edge.target}-${idx}`,
      source: edge.source,
      target: edge.target,
      sourceHandle: 'source-right',
      targetHandle: 'target-left',
      type: 'default',
      animated: true,
      style: {
        stroke: `var(--mantine-color-${dbtConfig.edgeColors[type] ?? 'gray'}-6)`,
      },
    }
  })

  return { nodes: [...laneDividers, ...laneLabels, ...flowNodes], edges: flowEdges }
}

function LineageGraph({
  nodes,
  edges,
  onNodeClick,
  onContextMenu,
  databricksTables,
  selectedNodeId,
  updateFrequency,
}: {
  nodes: LineageNode[]
  edges: LineageEdge[]
  onNodeClick?: (id: string) => void
  onContextMenu?: () => void
  databricksTables?: UnityCatalogTable[]
  selectedNodeId?: string
  updateFrequency?: UpdateFrequencyMap
}) {
  const { nodes: flowNodes, edges: flowEdges } = useMemo(
    () =>
      buildReactFlowElements(
        nodes,
        edges,
        onNodeClick,
        databricksTables,
        selectedNodeId,
        updateFrequency
      ),
    [nodes, edges, onNodeClick, databricksTables, selectedNodeId, updateFrequency]
  )

  const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null)

  // Whenever the selected node (or the layout itself) changes, re-center the
  // viewport on it so the selected node is always front and center rather
  // than wherever fitView happened to leave it.
  useEffect(() => {
    if (!selectedNodeId) return
    const instance = reactFlowInstanceRef.current
    const node = flowNodes.find((n) => n.id === selectedNodeId)
    if (!instance || !node) return
    const height = (node.data as { height?: number }).height ?? 0
    const x = node.position.x + NODE_WIDTH / 2
    const y = node.position.y + height / 2
    instance.setCenter(x, y, { zoom: instance.getZoom(), duration: 300 })
  }, [selectedNodeId, flowNodes])

  // Whenever the visible node set changes (e.g. via domain or source filter),
  // fit and center every currently visible node in the viewport so the
  // filtered set is always fully in view. This intentionally runs after
  // the effect above so it takes precedence when both the selected node
  // and a filter change together.
  useEffect(() => {
    const instance = reactFlowInstanceRef.current
    if (!instance || !flowNodes.length) return
    instance.fitView({
      nodes: flowNodes.map((node) => ({ id: node.id })),
      padding: 0.2,
      duration: 300,
    })
  }, [flowNodes])

  return (
    <Box
      style={{ height: 600 }}
      onContextMenu={(e) => {
        e.preventDefault()
        onContextMenu?.()
      }}
    >
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.05}
        maxZoom={2}
        attributionPosition="bottom-left"
        onInit={(instance) => {
          reactFlowInstanceRef.current = instance
        }}
      >
        <Background />
        <Controls />
      </ReactFlow>
    </Box>
  )
}

function LineageFlow({
  nodes,
  edges,
  title,
  onNodeClick,
  onContextMenu,
  databricksTables,
  selectedNodeId,
  selectedNode,
  columnLineage,
  updateFrequency,
}: {
  nodes?: LineageNode[]
  edges?: LineageEdge[]
  title?: string
  onNodeClick?: (id: string) => void
  onContextMenu?: () => void
  databricksTables?: UnityCatalogTable[]
  selectedNodeId?: string
  selectedNode?: LineageNode
  columnLineage?: ColumnLineageMap
  updateFrequency?: UpdateFrequencyMap
}) {
  const nodeList = nodes ?? []
  const edgeList = edges ?? []
  return (
    <Card withBorder>
      {title && (
        <Group mb="md">
          <ThemeIcon variant="light" color="prism">
            <IconGitBranch size={18} />
          </ThemeIcon>
          <Text fw={700}>{title}</Text>
        </Group>
      )}
      <Stack>
        <LineageGraph
          nodes={nodeList}
          edges={edgeList}
          onNodeClick={onNodeClick}
          onContextMenu={onContextMenu}
          databricksTables={databricksTables}
          selectedNodeId={selectedNodeId}
          updateFrequency={updateFrequency}
        />
        {selectedNode && (
          <NodeDetails node={selectedNode} columnLineage={columnLineage} updateFrequency={updateFrequency} />
        )}
      </Stack>
    </Card>
  )
}


function ColumnLineagePanel({
  nodeId,
  columnLineage,
  isSource = false,
}: {
  nodeId: string
  columnLineage?: ColumnLineageMap
  isSource?: boolean
}) {
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set())

  const entries: ColumnLineageEntry[] = React.useMemo(
    () => columnLineage?.get(nodeId) ?? [],
    [columnLineage, nodeId]
  )

  // Fetch compiled SQL lazily — enabled only when a Computed column is expanded
  const hasExpandedComputed = !isSource && entries.some(
    (e) => e.parents.length === 0 && expanded.has(e.name)
  )
  const compiledQuery = useQuery({
    queryKey: ['dbt-cloud', 'compiled-code', nodeId],
    queryFn: () => dbtCloudApi.getCompiledCode(nodeId),
    enabled: hasExpandedComputed,
    staleTime: 30 * 60 * 1000,
  })

  if (entries.length === 0)
    return <Empty message="No column metadata is available for this node." />

  const toggleColumn = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const columnsWithParents = entries.filter((e) => e.parents.length > 0)
  const columnsWithoutParents = entries.filter((e) => e.parents.length === 0)

  const manipulationColors: Record<string, string> = {
    Passthrough: 'green',
    Renamed: 'yellow',
    Computed: 'violet',
  }

  /** Infer how the column was derived from its parents. */
  const manipulation = (entry: ColumnLineageEntry): string => {
    if (entry.parents.length === 0) return 'Computed'
    const allMatch = entry.parents.every(
      (p) => p.column.toLowerCase() === entry.name.toLowerCase()
    )
    return allMatch ? 'Passthrough' : 'Renamed'
  }

  return (
    <Card withBorder>
      <Group gap="xs" mb="md">
        <ThemeIcon variant="light" color="prism">
          <IconColumns size={18} />
        </ThemeIcon>
        <Text fw={700}>Column lineage</Text>
        <Badge size="sm" variant="light" color="gray">
          {entries.length} columns
        </Badge>
        {columnsWithParents.length > 0 && (
          <Badge size="sm" variant="light" color="prism">
            {columnsWithParents.length} with upstream
          </Badge>
        )}
      </Group>

      <Table.ScrollContainer minWidth={isSource ? 400 : 600}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              {!isSource && <Table.Th style={{ width: 30 }} />}
              <Table.Th>Column</Table.Th>
              <Table.Th>Type</Table.Th>
              {!isSource && <Table.Th>Manipulation</Table.Th>}
              {!isSource && <Table.Th>Upstream</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {columnsWithParents.map((entry) => {
              const isExpanded = expanded.has(entry.name)
              const manip = manipulation(entry)
              return (
                <React.Fragment key={entry.name}>
                  <Table.Tr
                    style={isSource ? undefined : { cursor: 'pointer' }}
                    onClick={isSource ? undefined : () => toggleColumn(entry.name)}
                  >
                    {!isSource && (
                      <Table.Td>
                        {isExpanded ? (
                          <IconChevronDown size={14} />
                        ) : (
                          <IconChevronRight size={14} />
                        )}
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {entry.name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="gray" size="sm">
                        {entry.type ?? 'unknown'}
                      </Badge>
                    </Table.Td>
                    {!isSource && (
                      <Table.Td>
                        <Badge variant="light" color={manipulationColors[manip] ?? 'gray'} size="sm">
                          {manip}
                        </Badge>
                      </Table.Td>
                    )}
                    {!isSource && (
                      <Table.Td>
                        <Badge variant="light" color="prism" size="sm">
                          {entry.parents.length} parent{entry.parents.length !== 1 ? 's' : ''}
                        </Badge>
                      </Table.Td>
                    )}
                  </Table.Tr>
                  {!isSource && isExpanded &&
                    entry.parents.map((parent) => (
                      <Table.Tr
                        key={`${entry.name}-${parent.nodeId}-${parent.column}`}
                        bg="gray.0"
                      >
                        <Table.Td />
                        <Table.Td colSpan={4} pl="xl">
                          <Group gap="xs">
                            <IconGitBranch size={12} color="var(--mantine-color-prism-6)" />
                            <Text size="xs" c="dimmed">
                              {parent.nodeName}
                            </Text>
                            <Text size="xs" fw={600}>
                              {parent.column}
                            </Text>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                </React.Fragment>
              )
            })}
            {columnsWithoutParents.map((entry) => {
              const isExpanded = !isSource && expanded.has(entry.name)
              const snippet = isExpanded && compiledQuery.data
                ? extractColumnSnippet(compiledQuery.data, entry.name)
                : null
              return (
                <React.Fragment key={entry.name}>
                  <Table.Tr
                    style={isSource ? undefined : { cursor: 'pointer' }}
                    onClick={isSource ? undefined : () => toggleColumn(entry.name)}
                  >
                    {!isSource && (
                      <Table.Td>
                        {isExpanded ? (
                          <IconChevronDown size={14} />
                        ) : (
                          <IconChevronRight size={14} />
                        )}
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {entry.name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="gray" size="sm">
                        {entry.type ?? 'unknown'}
                      </Badge>
                    </Table.Td>
                    {!isSource && (
                      <Table.Td>
                        <Badge variant="light" color="violet" size="sm">
                          Computed
                        </Badge>
                      </Table.Td>
                    )}
                    {!isSource && (
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          —
                        </Text>
                      </Table.Td>
                    )}
                  </Table.Tr>
                  {isExpanded && (
                    <Table.Tr bg="gray.0">
                      <Table.Td />
                      <Table.Td colSpan={4} pl="xl">
                        {compiledQuery.isLoading ? (
                          <Group gap="xs" py="xs">
                            <Loader size={12} />
                            <Text size="xs" c="dimmed">Loading SQL…</Text>
                          </Group>
                        ) : snippet ? (
                          <Box
                            component="pre"
                            style={{
                              margin: 0,
                              padding: '8px 12px',
                              borderRadius: 4,
                              background: 'var(--mantine-color-gray-1)',
                              fontSize: 12,
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            <code
                              style={{
                                color: 'var(--mantine-color-violet-7)',
                                fontWeight: 600,
                              }}
                            >
                              {snippet.expression}
                            </code>
                            {snippet.from && (
                              <>
                                {'\n'}
                                <code style={{ color: 'var(--mantine-color-dimmed)' }}>
                                  {snippet.from}
                                </code>
                              </>
                            )}
                          </Box>
                        ) : (
                          <Text size="xs" c="dimmed">
                            Could not locate SQL expression for this column.
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  )}
                </React.Fragment>
              )
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  )
}

function MetricDetails({ node }: { node: LineageNode }) {
  const meta = node.metadata ?? {}
  const entries: Array<{ label: string; value: string }> = []
  if (meta.label) entries.push({ label: 'Label', value: meta.label })
  if (meta.metricType)
    entries.push({ label: 'Metric type', value: meta.metricType })
  if (meta.measure) entries.push({ label: 'Measure', value: meta.measure })
  if (meta.expression)
    entries.push({ label: 'Expression', value: meta.expression })
  if (meta.filter) entries.push({ label: 'Filter', value: meta.filter })
  if (meta.description)
    entries.push({ label: 'Description', value: meta.description })

  if (entries.length === 0) {
    return <Empty message="No metric definition metadata is available." />
  }

  return (
    <Table.ScrollContainer minWidth={300}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Property</Table.Th>
            <Table.Th>Value</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {entries.map((entry) => (
            <Table.Tr key={entry.label}>
              <Table.Td>
                <Text size="sm" fw={600}>
                  {entry.label}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {entry.value}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  )
}

function NodeDetails({
  node,
  columnLineage,
  updateFrequency,
}: {
  node?: LineageNode
  columnLineage?: ColumnLineageMap
  updateFrequency?: UpdateFrequencyMap
}) {
  const [compiledOpen, { open: openCompiled, close: closeCompiled }] =
    useDisclosure(false)
  const isSourceNode = node?.type === 'source' || node?.type === 'seed'
  const isMetricNode = node?.type === 'metric'
  const compiledQuery = useQuery({
    queryKey: ['dbt-cloud', 'compiled-code', node?.id],
    queryFn: () => dbtCloudApi.getCompiledCode(node?.id ?? ''),
    enabled:
      compiledOpen && Boolean(node?.id) && !isSourceNode && !isMetricNode,
  })

  if (!node) return null

  const compiledTitle = isMetricNode
    ? `Metric definition — ${node.name ?? node.id}`
    : `Compiled SQL — ${node.name ?? node.id}`

  return (
    <>
      <Card withBorder>
        <Text fw={700} mb="md">
          Node details
        </Text>
        <Stack gap="xs">
          <Group gap="xs">
            <Text size="sm" fw={600}>
              Name:
            </Text>
            <Text size="sm">{node.name ?? node.id}</Text>
          </Group>
          <Group gap="xs">
            <Text size="sm" fw={600}>
              Type:
            </Text>
            <Badge size="sm">{node.type ?? 'unknown'}</Badge>
          </Group>
          {node.system && (
            <Group gap="xs">
              <Text size="sm" fw={600}>
                System:
              </Text>
              <Text size="sm">{node.system}</Text>
            </Group>
          )}
          {updateFrequency?.frequencies.get(node.id) && (
            <Group gap="xs">
              <Text size="sm" fw={600}>
                Update frequency:
              </Text>
              <Badge size="sm" variant="light" color="blue" leftSection={<IconClock size={12} />}>
                {updateFrequency.frequencies.get(node.id)}
              </Badge>
            </Group>
          )}
          {updateFrequency?.lastRun.get(node.id) && (
            <Group gap="xs">
              <Text size="sm" fw={600}>
                Last run:
              </Text>
              <Text size="sm">
                {new Date(updateFrequency.lastRun.get(node.id)!).toLocaleString()}
              </Text>
            </Group>
          )}
        </Stack>

        <Button
          mt="md"
          variant="light"
          leftSection={
            isMetricNode ? <IconTarget size={16} /> : <IconCode size={16} />
          }
          onClick={openCompiled}
        >
          {isMetricNode ? 'Show metric definition' : 'Show compiled SQL transformation'}
        </Button>

        {isMetricNode ? (
          <Box mt="md">
            <MetricDetails node={node} />
          </Box>
        ) : (
          <Box mt="md">
            <ColumnLineagePanel nodeId={node.id} columnLineage={columnLineage} isSource={isSourceNode} />
          </Box>
        )}
      </Card>

      <Modal
        opened={compiledOpen}
        onClose={closeCompiled}
        title={compiledTitle}
        size="xl"
      >
        <Box style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {isMetricNode ? (
            <MetricDetails node={node} />
          ) : isSourceNode ? (
            <Text size="sm" c="dimmed">
              This is a {node.type} node and does not have dbt transformations
              or compiled SQL.
            </Text>
          ) : compiledQuery.isLoading ? (
            <Group justify="center" py="md">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading compiled SQL…
              </Text>
            </Group>
          ) : compiledQuery.error ? (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {compiledQuery.error instanceof Error
                ? compiledQuery.error.message
                : 'Failed to load compiled SQL.'}
            </Alert>
          ) : compiledQuery.data ? (
            <SqlEditor
              value={compiledQuery.data}
              onChange={() => {}}
              height="60vh"
              readOnly
            />
          ) : (
            <Text size="sm" c="dimmed">
              No compiled SQL is available for this node. Compiled code is only
              generated for dbt models that have been run.
            </Text>
          )}
        </Box>
      </Modal>
    </>
  )
}

export function Lineage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const assetId = searchParams.get('asset')?.trim() ?? ''
  const domainFilter = searchParams.get('domain')?.trim() || null
  const sourceFilter = searchParams.get('source')?.trim() || null

  const assetsQuery = useQuery({
    queryKey: ['dbt-cloud', 'assets'],
    queryFn: () => dbtCloudApi.listAssets(),
  })

  const lineageQuery = useQuery({
    queryKey: ['dbt-cloud', 'lineage'],
    queryFn: () => dbtCloudApi.getLineage(),
  })

  // Resolve the asset ID to a dbt asset. The Intelligence API may return IDs
  // in Databricks format (catalog.schema.table) while the lineage page works
  // with dbt unique IDs (model.project.name). When an exact match isn't found,
  // fall back to matching the trailing name segment so clicking a Databricks
  // asset from Global Search still locates the corresponding dbt model.
  const { dbtAsset, resolvedAssetId } = useMemo(() => {
    const assets = assetsQuery.data ?? []
    const exact = assets.find((a) => a.id === assetId)
    if (exact) return { dbtAsset: exact, resolvedAssetId: assetId }

    if (!assetId) return { dbtAsset: undefined, resolvedAssetId: '' }

    const tableName = assetId.split('.').pop()?.toLowerCase() ?? ''
    const byName = assets.find((a) => a.name.toLowerCase() === tableName)
    return {
      dbtAsset: byName,
      resolvedAssetId: byName?.id ?? assetId,
    }
  }, [assetsQuery.data, assetId])

  const sourceOptions = useMemo(() => {
    const edges = lineageQuery.data?.edges ?? []
    // Collect node IDs that are the source end of at least one edge
    const hasDownstream = new Set(edges.map((e) => e.source))
    const sources = new Set<string>()
    for (const node of lineageQuery.data?.nodes ?? []) {
      if (isSourceNode(node) && node.system && hasDownstream.has(node.id))
        sources.add(node.system)
    }
    return Array.from(sources)
      .sort()
      .map((src) => ({ value: src, label: src }))
  }, [lineageQuery.data])

  // Pre-compute the set of node IDs reachable from the selected source so
  // the domain and asset dropdowns can be narrowed to relevant items.
  const sourceReachableIds = useMemo(() => {
    if (!sourceFilter) return null
    const allNodes = lineageQuery.data?.nodes ?? []
    const allEdges = lineageQuery.data?.edges ?? []
    const { nodes: reachable } = filterBySource(allNodes, allEdges, sourceFilter)
    return new Set(reachable.map((n) => n.id))
  }, [lineageQuery.data, sourceFilter])

  const assetLineageQuery = useQuery({
    queryKey: ['dbt-cloud', 'lineage', resolvedAssetId],
    queryFn: () => dbtCloudApi.getAssetLineage(resolvedAssetId),
    enabled: Boolean(resolvedAssetId),
  })

  const domainOptions = useMemo(() => {
    // When an asset is selected, scope domains to its lineage neighborhood;
    // otherwise use the full lineage (optionally narrowed by source filter).
    const pool = assetLineageQuery.data?.nodes ?? lineageQuery.data?.nodes ?? []
    const domains = new Set<string>()
    for (const node of pool) {
      // When a source filter is active, only include domains from nodes
      // reachable from that source.
      if (sourceReachableIds && !sourceReachableIds.has(node.id)) continue
      // Domainless nodes (sources/seeds/semantic models) don't belong to a
      // schema domain -- skip them so non-domain values like "dbt" never
      // become selectable schema-domain options.
      if (isDomainlessNode(node)) continue
      const domain = schemaDomain(node.system)
      if (domain) domains.add(domain)
    }
    return Array.from(domains)
      .sort()
      .map((domain) => ({ value: domain, label: domain }))
  }, [lineageQuery.data, assetLineageQuery.data, sourceReachableIds])

  const selectOptions = useMemo(
    () =>
      assetsQuery.data
        ?.filter(
          (asset) =>
            // When a source filter is active, only show assets reachable
            // from the selected source system.
            (!sourceReachableIds || sourceReachableIds.has(asset.id)) &&
            (!domainFilter ||
              schemaDomain(asset.owner) === domainFilter ||
              // Semantic-layer assets have no schema domain, so keep them
              // selectable even when a domain filter is applied.
              asset.type === 'semantic')
        )
        .map((asset) => ({
          value: asset.id,
          label: `${asset.name} (${asset.type ?? 'unknown'})`,
        })) ?? [],
    [assetsQuery.data, domainFilter, sourceReachableIds]
  )

  const activeLineage = assetLineageQuery.data ?? lineageQuery.data

  const filteredLineage = useMemo(() => {
    const byDomain = filterByDomain(
      activeLineage?.nodes ?? [],
      activeLineage?.edges ?? [],
      domainFilter
    )
    return filterBySource(byDomain.nodes, byDomain.edges, sourceFilter)
  }, [activeLineage, domainFilter, sourceFilter])

  const selectedNode = useMemo(
    () => filteredLineage.nodes.find((node) => node.id === resolvedAssetId),
    [filteredLineage, resolvedAssetId]
  )

  const databricksTablesQuery = useQuery({
    queryKey: ['databricks', 'uc', 'tables', 'all'],
    queryFn: listAllTables,
  })

  const columnLineageQuery = useQuery({
    queryKey: ['dbt-cloud', 'column-lineage'],
    queryFn: () => dbtCloudApi.getColumnLineage(),
    enabled: Boolean(resolvedAssetId),
    staleTime: 30 * 60 * 1000,
  })

  const updateFrequencyQuery = useQuery({
    queryKey: ['dbt-cloud', 'update-frequency', resolvedAssetId ?? ''],
    queryFn: () => dbtCloudApi.getUpdateFrequency(resolvedAssetId),
    staleTime: 10 * 60 * 1000,
  })

  const selectAsset = (value: string | null) => {
    const id = value?.trim() ?? ''
    const next = new URLSearchParams(searchParams)
    if (id) {
      next.set('asset', id)
      // Keep the schema domain filter in sync with the selected asset so it
      // always reflects the asset's own domain rather than a stale value.
      const asset = assetsQuery.data?.find((a) => a.id === id)
      // Domainless assets (sources/seeds/semantic models) have no schema
      // domain, so selecting one clears the domain filter rather than setting
      // it to a non-domain value like "dbt".
      const domain =
        asset && !isDomainlessNode({ id: asset.id, type: asset.type })
          ? schemaDomain(asset.owner)
          : undefined
      if (domain) {
        next.set('domain', domain)
      } else {
        next.delete('domain')
      }

      // Auto-select the source system that feeds into this asset by walking
      // upstream from the asset to find connected source nodes.
      const allNodes = lineageQuery.data?.nodes ?? []
      const allEdges = lineageQuery.data?.edges ?? []
      const parentsMap = new Map<string, string[]>()
      for (const edge of allEdges) {
        if (!parentsMap.has(edge.target)) parentsMap.set(edge.target, [])
        parentsMap.get(edge.target)!.push(edge.source)
      }
      const visited = new Set<string>()
      const queue = [id]
      const upstreamSources = new Set<string>()
      const nodeMap = new Map(allNodes.map((n) => [n.id, n]))
      while (queue.length > 0) {
        const current = queue.shift()!
        if (visited.has(current)) continue
        visited.add(current)
        const node = nodeMap.get(current)
        if (node && isSourceNode(node) && node.system) {
          upstreamSources.add(node.system)
        }
        for (const parent of parentsMap.get(current) ?? []) {
          queue.push(parent)
        }
      }
      // If the asset traces back to exactly one source system, auto-select it
      if (upstreamSources.size === 1) {
        next.set('source', [...upstreamSources][0])
      } else {
        next.delete('source')
      }
    } else {
      next.delete('asset')
    }
    setSearchParams(next)
  }

  const clearAsset = () => selectAsset(null)

  const selectDomain = (value: string | null) => {
    const domain = value?.trim() ?? ''
    const next = new URLSearchParams(searchParams)
    if (domain) {
      next.set('domain', domain)

      // Walk upstream from all nodes in this domain to find the source systems
      const allNodes = lineageQuery.data?.nodes ?? []
      const allEdges = lineageQuery.data?.edges ?? []
      const domainNodeIds = allNodes
        .filter((n) => !isDomainlessNode(n) && schemaDomain(n.system) === domain)
        .map((n) => n.id)
      const parentsMap = new Map<string, string[]>()
      for (const edge of allEdges) {
        if (!parentsMap.has(edge.target)) parentsMap.set(edge.target, [])
        parentsMap.get(edge.target)!.push(edge.source)
      }
      const visited = new Set<string>()
      const queue = [...domainNodeIds]
      const upstreamSources = new Set<string>()
      const nodeMap = new Map(allNodes.map((n) => [n.id, n]))
      while (queue.length > 0) {
        const current = queue.shift()!
        if (visited.has(current)) continue
        visited.add(current)
        const node = nodeMap.get(current)
        if (node && isSourceNode(node) && node.system) {
          upstreamSources.add(node.system)
        }
        for (const parent of parentsMap.get(current) ?? []) {
          queue.push(parent)
        }
      }
      if (upstreamSources.size === 1) {
        next.set('source', [...upstreamSources][0])
      } else {
        next.delete('source')
      }
    } else {
      next.delete('domain')
    }
    setSearchParams(next)
  }

  const selectSource = (value: string | null) => {
    const src = value?.trim() ?? ''
    const next = new URLSearchParams(searchParams)
    // Reset asset and domain when the source changes so stale
    // selections from a different source don't persist.
    next.delete('asset')
    next.delete('domain')
    if (src) {
      next.set('source', src)
    } else {
      next.delete('source')
    }
    setSearchParams(next)
  }

  return (
    <Stack>
      <IntelligencePageHeader
        title="Data lineage"
        description="Explore lineage from the Prism data model. Select an asset to focus on its upstream and downstream dependencies."
      />
      <Group grow align="flex-end">
        <Select
          searchable
          clearable
          value={sourceFilter}
          onChange={selectSource}
          data={sourceOptions}
          disabled={lineageQuery.isLoading}
          placeholder="Filter by source system"
          label="Source"
          leftSection={<IconPlug size={16} />}
        />
        <Select
          searchable
          clearable
          value={domainFilter}
          onChange={selectDomain}
          data={domainOptions}
          disabled={lineageQuery.isLoading}
          placeholder="Filter by schema domain"
          label="Schema domain"
          leftSection={<IconDatabase size={16} />}
        />
        <Select
          searchable
          clearable
          value={resolvedAssetId || null}
          onChange={selectAsset}
          data={selectOptions}
          disabled={assetsQuery.isLoading}
          placeholder="Search dbt assets by name or type"
          label="Asset"
          leftSection={<IconSearch size={16} />}
        />
      </Group>

      <QueryState error={assetsQuery.error} loading={assetsQuery.isLoading} />

      {!resolvedAssetId && (
        <>
          <QueryState
            error={lineageQuery.error}
            loading={lineageQuery.isLoading}
          />
          {lineageQuery.data && (
            <RenderErrorBoundary>
              <LineageFlow
                nodes={filteredLineage.nodes}
                edges={filteredLineage.edges}
                title="Data lineage"
                onNodeClick={(id) => selectAsset(id)}
                onContextMenu={clearAsset}
                databricksTables={databricksTablesQuery.data}
                selectedNodeId={resolvedAssetId}
                updateFrequency={updateFrequencyQuery.data}
              />
            </RenderErrorBoundary>
          )}
        </>
      )}

      {resolvedAssetId && (
        <>
          <QueryState
            error={assetLineageQuery.error}
            loading={assetLineageQuery.isLoading}
          />
          {assetLineageQuery.data && (
            <RenderErrorBoundary>
              <LineageFlow
                nodes={filteredLineage.nodes}
                edges={filteredLineage.edges}
                title={`Lineage for ${dbtAsset?.name ?? resolvedAssetId}`}
                onNodeClick={(id) => selectAsset(id)}
                onContextMenu={clearAsset}
                databricksTables={databricksTablesQuery.data}
                selectedNodeId={resolvedAssetId}
                selectedNode={selectedNode}
                columnLineage={columnLineageQuery.data}
                updateFrequency={updateFrequencyQuery.data}
              />
            </RenderErrorBoundary>
          )}
        </>
      )}
    </Stack>
  )
}
